import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPublishedBundles, getEventPublishedBundles, getClubEvents, activateBundle, getEventDoc, setEventPreviewBundle, clearEventPreviewBundle } from '../lib/firebase.js';
import { describeBundleVisibility } from '../lib/bundleVisibility.js';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

export default function VersionHistory() {
  const { clubId } = useParams();
  const [allBundles, setAllBundles] = useState([]);
  const [filterEvent, setFilterEvent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activating, setActivating] = useState(null);
  const [settingPreview, setSettingPreview] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [activeVersions, setActiveVersions] = useState({});
  const [previewVersions, setPreviewVersions] = useState({});

  useEffect(() => {
    async function load() {
      try {
        // Load from both legacy (courseBundles) and new (events/*/bundle) paths
        const [legacyPublished, events] = await Promise.all([
          getPublishedBundles(clubId),
          getClubEvents(clubId),
        ]);
        const eventBundleArrays = await Promise.all(
          events.map((ev) => getEventPublishedBundles(clubId, ev.id)),
        );
        const eventBundles = eventBundleArrays.flat();
        // Merge, dedup by (event_id, id). Doc IDs are shared across events
        // (every event has its own v1, v2, ...) so deduping on id alone
        // collapses later events into the first. Legacy bundles have no
        // event_id — key on id only for those.
        const seen = new Set();
        const merged = [];
        const keyOf = (b) => b.event_id ? `${b.event_id}/${b.id}` : b.id;
        for (const b of [...eventBundles, ...legacyPublished]) {
          const k = keyOf(b);
          if (!seen.has(k)) {
            seen.add(k);
            merged.push(b);
          }
        }
        merged.sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
        setAllBundles(merged);
        // Load active + app-preview version for each event
        const eventVersions = {};
        const previewVers = {};
        await Promise.all(
          events.map(async (ev) => {
            const eventDoc = await getEventDoc(clubId, ev.id);
            if (eventDoc && eventDoc.content_version != null) {
              eventVersions[ev.id] = eventDoc.content_version;
            }
            if (eventDoc && eventDoc.preview_content_version != null) {
              previewVers[ev.id] = eventDoc.preview_content_version;
            }
          }),
        );
        setActiveVersions(eventVersions);
        setPreviewVersions(previewVers);
      } catch (err) {
        setError('Failed to load version history: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [clubId]);

  const eventIds = useMemo(
    () => [...new Set(allBundles.map((b) => b.event_id).filter(Boolean))].sort(),
    [allBundles],
  );

  const bundles = useMemo(
    () => filterEvent ? allBundles.filter((b) => b.event_id === filterEvent) : allBundles,
    [allBundles, filterEvent],
  );

  async function handleActivate(bundle) {
    const eventId = bundle.event_id;
    const version = bundle.version;
    if (!eventId || version == null) {
      setError('Cannot activate: bundle is missing event_id or version.');
      return;
    }
    if (!confirm(`Activate v${version} for event "${eventId}"? This makes it the live version for all users.`)) {
      return;
    }

    setActivating(bundle.id);
    setError('');
    setSuccessMsg('');

    try {
      await activateBundle(clubId, eventId, version);
      setActiveVersions((prev) => ({ ...prev, [eventId]: version }));
      setSuccessMsg(`Activated v${version} for event "${eventId}".`);
    } catch (err) {
      setError('Activation failed: ' + (err.message || err));
    } finally {
      setActivating(null);
    }
  }

  async function handleSetPreview(bundle) {
    const eventId = bundle.event_id;
    const version = bundle.version;
    if (!eventId || version == null) {
      setError('Cannot set preview: bundle is missing event_id or version.');
      return;
    }
    if (!confirm(`Set v${version} as the APP PREVIEW for event "${eventId}"?\n\nThis does NOT make it live. Club admins — including a race officer reviewing the setup — can load this rendered version in the app (Club Content → load preview bundle) and sign it off before you activate it.`)) {
      return;
    }

    setSettingPreview(bundle.id);
    setError('');
    setSuccessMsg('');

    try {
      await setEventPreviewBundle(clubId, eventId, version);
      setPreviewVersions((prev) => ({ ...prev, [eventId]: version }));
      setSuccessMsg(`Set v${version} as app preview for "${eventId}". Admins can now preview it in the app before activating.`);
    } catch (err) {
      setError('Set preview failed: ' + (err.message || err));
    } finally {
      setSettingPreview(null);
    }
  }

  async function handleClearPreview(eventId) {
    if (!confirm(`Clear the app preview for event "${eventId}"? Admins will fall back to the active version.`)) {
      return;
    }
    setError('');
    setSuccessMsg('');
    try {
      await clearEventPreviewBundle(clubId, eventId);
      setPreviewVersions((prev) => { const next = { ...prev }; delete next[eventId]; return next; });
      setSuccessMsg(`Cleared app preview for "${eventId}".`);
    } catch (err) {
      setError('Clear preview failed: ' + (err.message || err));
    }
  }

  function formatDate(ts) {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds ? ts.seconds * 1000 : ts);
    return date.toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (loading) {
    return (
      <div className="page">
        <p>Loading version history...</p>
      </div>
    );
  }

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo={`/clubs/${clubId}/course-cards`} backLabel="Course Cards" title="Version History" />

      {error && <div className="error-banner" role="alert">{error}</div>}
      {successMsg && <div className="rt-success-banner" role="status">{successMsg}</div>}

      {eventIds.length > 1 && (
        <div className="filter-bar">
          <label className="form-field-inline">
            <span className="form-label">Event:</span>
            <select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)}>
              <option value="">All events</option>
              {eventIds.map((eid) => (
                <option key={eid} value={eid}>{eid}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {allBundles.length === 0 ? (
        <p className="empty-state">No published versions yet.</p>
      ) : bundles.length === 0 ? (
        <p className="empty-state">No versions for this event.</p>
      ) : (
        <table className="version-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Event</th>
              <th>Status</th>
              <th>Cards</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bundles.map((b) => {
              const isActive = b.event_id && activeVersions[b.event_id] === b.version;
              const isPreview = b.event_id && previewVersions[b.event_id] === b.version;
              // Named draft→preview→live visibility state (ra-gbrd.23). The
              // primary state is single-valued (live > preview > published);
              // 'draft' lives on the Course Cards page (WIP), not in this
              // published-version table.
              const visibility = describeBundleVisibility({
                version: b.version,
                activeVersion: b.event_id ? activeVersions[b.event_id] : null,
                previewVersion: b.event_id ? previewVersions[b.event_id] : null,
              });
              // Key on (event_id, id): doc ids collide across events (every
              // event has its own v1, v2, ...) so a bare id key lets React
              // reuse stale rows from other events when the filter changes.
              const rowKey = b.event_id ? `${b.event_id}/${b.id}` : b.id;
              return (
                <tr key={rowKey} className={isActive ? 'active-row' : ''}>
                  <td>
                    <strong>v{b.version}</strong>
                  </td>
                  <td>
                    <code>{b.event_id}</code>
                  </td>
                  <td>
                    <span className={`badge badge-${visibility.state}`} title={visibility.hint}>
                      {visibility.label}
                    </span>
                  </td>
                  <td>{(b.course_cards || []).length}</td>
                  <td>{formatDate(b.published_at || b.created_at)}</td>
                  <td className="action-cell">
                    <Link
                      to={b.event_id ? `/clubs/${clubId}/events/${b.event_id}/published/${b.id}` : `/clubs/${clubId}/courses/published/${b.id}`}
                      className="btn btn-sm"
                    >
                      View
                    </Link>
                    {!isActive && (
                      <button
                        className="btn btn-sm"
                        style={{ background: '#16a34a', color: '#fff' }}
                        onClick={() => handleActivate(b)}
                        disabled={activating === b.id}
                      >
                        {activating === b.id ? 'Activating...' : 'Activate'}
                      </button>
                    )}
                    {b.event_id && b.version != null && !isPreview && (
                      <button
                        className="btn btn-sm"
                        style={{ background: '#7c3aed', color: '#fff' }}
                        onClick={() => handleSetPreview(b)}
                        disabled={settingPreview === b.id}
                        title="Point the app's preview at this version (does not activate). Club admins — including a reviewing race officer — can then load the rendered bundle in the app before it goes live."
                      >
                        {settingPreview === b.id ? 'Setting…' : 'Set as app preview'}
                      </button>
                    )}
                    {isPreview && (
                      <button
                        className="btn btn-sm"
                        onClick={() => handleClearPreview(b.event_id)}
                        title="Remove the app-preview pointer for this event"
                      >
                        Clear preview
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      </div>
    </>
  );
}
